import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { complaintService } from '../services/api';
import { Complaint } from '../types';
import './TenantComplaints.css';

const CATEGORIES = [
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'CLEANLINESS', label: 'Cleanliness' },
  { value: 'NOISE', label: 'Noise' },
  { value: 'AMENITIES', label: 'Amenities' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'OTHERS', label: 'Others' },
];

const STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CANCELLED: 'Cancelled',
  CLOSED: 'Closed',
};

function categoryLabel(cat?: string): string {
  return CATEGORIES.find((c) => c.value === cat)?.label || cat || '—';
}

function statusLabel(status?: string): string {
  const s = String(status ?? 'NEW').toUpperCase();
  if (s === 'CLOSED') return 'Cancelled';
  return STATUS_LABELS[s] || s;
}

function formatDateTime(val?: string): string {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatLocation(c: Complaint): string {
  const parts: string[] = [];
  if (c.floor) {
    const n = String(c.floor.floorNumber ?? '').trim();
    parts.push(n ? `Floor ${n}` : 'Floor');
  }
  if (c.room) parts.push(`Room ${c.room.roomNumber}`);
  if (c.bed) parts.push(`Bed ${c.bed.bedNumber}`);
  return parts.length ? parts.join(' · ') : '—';
}

function getComplaintPhotoUrl(complaint: Complaint): string | null {
  const att = complaint.attachments;
  if (!att || !Array.isArray(att) || att.length === 0) return null;
  const first = att[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object' && 'url' in first && first.url) return String(first.url);
  return null;
}

const TenantComplaints: React.FC = () => {
  const { hostel, user } = useAuth();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Complaint | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'MAINTENANCE',
  });

  const loadComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const list = await complaintService.getMyComplaints();
      setComplaints(list);
    } catch {
      toast.error('Failed to load complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadComplaints();
  }, [loadComplaints]);

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const closeCreate = () => {
    if (submitting) return;
    clearPhoto();
    setForm({ title: '', description: '', category: 'MAINTENANCE' });
    setShowCreate(false);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    const title = form.title.trim();
    const description = form.description.trim();
    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('hostelId', String(hostel.id));
      fd.append('title', title);
      fd.append('description', description);
      fd.append('category', form.category);
      fd.append('priority', 'MEDIUM');
      if (photoFile) fd.append('photo', photoFile);
      if (user?.floor) fd.append('floorId', String(user.floor));
      if (user?.room) fd.append('roomId', String(user.room));
      if (user?.bedId) fd.append('bedId', String(user.bedId));

      await complaintService.createComplaint(fd);
      toast.success('Complaint submitted. The hostel will review it soon.');
      closeCreate();
      await loadComplaints();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading complaints…</div>;
  }

  return (
    <div className="tc-page">
      <header className="tc-header">
        <div>
          <h1>Complaints</h1>
          <p className="tc-header__sub">Raise issues for your hostel — only you can see your complaints here</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          New complaint
        </button>
      </header>

      {complaints.length === 0 ? (
        <p className="tc-empty">No complaints yet. Tap “New complaint” to report an issue.</p>
      ) : (
        <ul className="tc-list">
          {complaints.map((c) => {
            const status = String(c.status ?? 'NEW').toLowerCase().replace('_', '-');
            return (
              <li key={c.id}>
                <button type="button" className="tc-row" onClick={() => setSelected(c)}>
                  <div className="tc-row__main">
                    <span className="tc-row__title">{c.title}</span>
                    <span className="tc-row__meta">
                      {categoryLabel(c.category)} · {formatDateTime(c.createdAt)}
                    </span>
                  </div>
                  <span className={`tc-status tc-status--${status}`}>{statusLabel(c.status)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {showCreate ? (
        <div className="tc-modal-overlay" role="presentation" onClick={closeCreate}>
          <div className="tc-modal" role="dialog" aria-labelledby="tc-create-title" onClick={(e) => e.stopPropagation()}>
            <div className="tc-modal__head">
              <h2 id="tc-create-title">New complaint</h2>
              <button type="button" className="tc-modal__close" onClick={closeCreate} aria-label="Close">
                ×
              </button>
            </div>
            <form className="tc-form" onSubmit={(e) => void handleCreate(e)}>
              <label className="tc-field">
                <span>Title</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Brief summary"
                  maxLength={120}
                  required
                />
              </label>
              <label className="tc-field">
                <span>Category</span>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="tc-field">
                <span>Description</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                  placeholder="Describe the issue in detail"
                  required
                />
              </label>
              <label className="tc-field">
                <span>Photo (optional)</span>
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="tc-photo-preview" />
                ) : null}
              </label>
              <div className="tc-modal__actions">
                <button type="button" className="btn" onClick={closeCreate} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {selected ? (
        <div className="tc-modal-overlay" role="presentation" onClick={() => setSelected(null)}>
          <div className="tc-modal tc-modal--detail" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="tc-modal__head">
              <h2>{selected.title}</h2>
              <button type="button" className="tc-modal__close" onClick={() => setSelected(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="tc-detail">
              <p>
                <span className="tc-detail__label">Status</span>
                <span className={`tc-status tc-status--${String(selected.status ?? 'NEW').toLowerCase().replace('_', '-')}`}>
                  {statusLabel(selected.status)}
                </span>
              </p>
              <p>
                <span className="tc-detail__label">Category</span>
                {categoryLabel(selected.category)}
              </p>
              <p>
                <span className="tc-detail__label">Submitted</span>
                {formatDateTime(selected.createdAt)}
              </p>
              <p>
                <span className="tc-detail__label">Location</span>
                {formatLocation(selected)}
              </p>
              <div className="tc-detail__block">
                <span className="tc-detail__label">Description</span>
                <p className="tc-detail__text">{selected.description}</p>
              </div>
              {selected.resolutionNotes ? (
                <div className="tc-detail__block tc-detail__block--resolved">
                  <span className="tc-detail__label">Hostel response</span>
                  <p className="tc-detail__text">{selected.resolutionNotes}</p>
                </div>
              ) : null}
              {getComplaintPhotoUrl(selected) ? (
                <div className="tc-detail__block">
                  <span className="tc-detail__label">Photo</span>
                  <a href={getComplaintPhotoUrl(selected)!} target="_blank" rel="noopener noreferrer">
                    <img src={getComplaintPhotoUrl(selected)!} alt="Complaint" className="tc-detail__photo" />
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TenantComplaints;
